import { useCriticalityData } from "@features/ax/assetRegistry/networks/hooks";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useUpdateNetworkCriticality } from "../../hooks";

interface CriticalityAssessmentData {
  criticalityScores: Record<string, number>;
  criticalityAssessment: Array<{
    name: string;
    score: number;
    code: string;
    weight: number;
  }>;
  finalScore: number;
  level: "Low" | "Medium" | "High" | "Critical";
}

interface NetworkCriticalityModalProps {
  open: boolean;
  onClose: () => void;
  existingScores?: Record<string, number>;
  existingFinalScore?: number;
  title?: string;
  editingType?: any;
}

const NetworkCriticalityModal: React.FC<NetworkCriticalityModalProps> = ({
  open,
  onClose,
  existingScores,
  existingFinalScore,
  title = "Criticality Assessment",
  editingType,
}) => {
  const remoteUtilityId = getRemoteUtilityId();
  const dataLoadedRef = useRef(false);

  const initializationRef = useRef(false);
  const { mutateAsync: updateNetworkCriticality, isPending: isUpdating } =
    useUpdateNetworkCriticality();
  const [criticalityLevel, setCriticalityLevel] = useState<
    "Low" | "Medium" | "High" | "Critical"
  >("Low");
  const [criticalityScore, setCriticalityScore] = useState(0);
  const [criticalityData, setCriticalityData] = useState<{
    [key: string]: number;
  }>({});

  // Fetch criticality data from API
  const {
    data: criticalityApiData,
    isLoading: isCriticalityLoading,
    isError: isCriticalityError,
  } = useCriticalityData({
    remote_utility_id: remoteUtilityId,
    show_inactive: "false",
  });

  // Initialize dynamic criticality fields with default value of 3
  const initializeDynamicFields = useMemo(() => {
    if (
      !criticalityApiData?.result ||
      !Array.isArray(criticalityApiData.result)
    )
      return {};

    const dynamicFields: any = {};

    // 🎯 Step 1: Determine the Source Array and whether to Filter
    const isEditing = !!editingType?.criticalityAssessment;
    const sourceArray = isEditing
      ? editingType.criticalityAssessment
      : criticalityApiData.result;

    // 🎯 Step 2: Apply Conditional Filtering and Initialization

    if (sourceArray) {
      let itemsToProcess = sourceArray;

      // Only apply the isActive filter if we are NOT using the editing list
      if (!isEditing) {
        itemsToProcess = sourceArray.filter(
          (item: any) => item.isActive === true,
        );
      }

      itemsToProcess.forEach((item: any) => {
        dynamicFields[item.code] = 3; // Default to 3
      });
    }

    return dynamicFields;
  }, [criticalityApiData]);

  // Reset initialization when modal closes
  useEffect(() => {
    if (!open) {
      initializationRef.current = false;
    }
  }, [open]);

  // Initialize criticality data with default score of 3 (when no existing scores)
  useEffect(() => {
    if (
      criticalityApiData?.result &&
      Object.keys(criticalityData).length === 0 &&
      !existingScores
    ) {
      const initialData: { [key: string]: number } = {};
      criticalityApiData.result.forEach((item: any) => {
        if (item.isActive) {
          initialData[item.code] = 3; // Default to 3
        }
      });
      setCriticalityData(initialData);
    }
  }, [criticalityApiData, criticalityData, existingScores]);

  // Calculate criticality score using the correct formula
  useEffect(() => {
    if (criticalityApiData?.result && Object.keys(criticalityData).length > 0) {
      // Formula: ((weight/100) * score) add all Assessment Parameters then multiply *20
      const score = criticalityApiData.result.reduce(
        (total: number, item: any) => {
          if (item.isActive && criticalityData[item.code] !== undefined) {
            // Apply the formula: (weight/100) * score
            const parameterScore =
              (item.weightage / 100) * criticalityData[item.code];
            return total + parameterScore;
          }
          return total;
        },
        0,
      );

      const finalScore = score * 20;

      setCriticalityScore(Math.round(finalScore));

      // Determine criticality level based on the score
      let level: "Low" | "Medium" | "High" | "Critical";
      if (finalScore <= 25) level = "Low";
      else if (finalScore <= 50) level = "Medium";
      else if (finalScore <= 75) level = "High";
      else level = "Critical";

      setCriticalityLevel(level);
    }
  }, [criticalityData, criticalityApiData, editingType]);

  // Create form instance
  const form = FormService.createForm({
    fields: [],
    defaultValues: criticalityData,
  });

  useEffect(() => {
    if (
      criticalityApiData?.result &&
      Array.isArray(criticalityApiData.result)
    ) {
      const criticalityScores: Record<string, number> = {};

      if (
        editingType?.criticalityAssessment &&
        Array.isArray(editingType.criticalityAssessment)
      ) {
        // Case 1: editingType.criticalityAssessment exists - use existing scores
        editingType.criticalityAssessment.forEach((item: any) => {
          if (item.code && item.score !== undefined) {
            criticalityScores[item.code] = item.score;
          }
        });
      } else {
        // Case 2: editingType.criticalityAssessment doesn't exist - use default score of 3
        criticalityApiData.result
          .filter((item: any) => item.isActive === true)
          .forEach((item: any) => {
            criticalityScores[item.code] = 3; // Default to 3
          });
      }

      // For dynamic criticality fields, use scores from criticality_assessment or default to 3
      const finalCriticalityData = Object.keys(initializeDynamicFields).reduce(
        (acc, key) => {
          acc[key] =
            criticalityScores[key] !== undefined ? criticalityScores[key] : 3;
          return acc;
        },
        {} as any,
      );

      setCriticalityData(finalCriticalityData);

      // Set form values directly
      try {
        Object.keys(finalCriticalityData).forEach((key) => {
          form.setValue(key, finalCriticalityData[key]);
        });
      } catch (error) {
        console.error("Error setting form values:", error);
      }

      dataLoadedRef.current = true;
    }
  }, [editingType, criticalityApiData, initializeDynamicFields, form]);

  const formFields: FormField[] = editingType?.criticalityAssessment
    ? // 1. If editingType?.criticalityAssessment exists, use it.
    editingType.criticalityAssessment.map((item: any) => ({
      name: item.code,
      label: item.name,
      type: "slider",
      min: 1,
      max: 5,
      step: 1,
      showHelperTooltip: true,
      tooltipText: item.description || `Assessment for ${item.name}`,
      // Assuming criticalityData[item.code] or 3 is the default/current value you want
      defaultValue: criticalityData[item.code] || 3,
      // suffix: `(${item.weightage}% weight)`,
      helperText:
        item.description || "Rate from 1 (Very Low) to 5 (Very High).",
      classes: {
        container: "w-full mb-6",
        label: "block text-sm font-medium text-gray-700 mb-3",
        slider:
          "w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-2",
      },
    }))
    : // 2. Otherwise, use criticalityApiData?.result as before (your original logic).
    criticalityApiData?.result
      ?.filter((item: any) => item.isActive)
      .map((item: any) => ({
        name: item.code,
        label: item.name,
        type: "slider",
        min: 1,
        max: 5,
        step: 1,
        showHelperTooltip: true,
        tooltipText: item.description || `Assessment for ${item.name}`,
        defaultValue: criticalityData[item.code] || 3,
        suffix: `(${item?.weightage}% weight)`,
        helperText:
          item.description || "Rate from 1 (Very Low) to 5 (Very High).",
        classes: {
          container: "w-full mb-6",
          label: "block text-sm font-medium text-gray-700 mb-3",
          slider:
            "w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-xs text-gray-500 mt-2",
        },
      })) || [];

  const handleFieldChange = (fieldName: string, value: any) => {
    setCriticalityData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleSave = async () => {
    // Prepare the criticality assessment data
    const criticalityAssessment =
      criticalityApiData?.result
        ?.filter((item: any) => item.isActive)
        .map((item: any) => ({
          name: item.name,
          score: criticalityData[item.code] || 3,
          code: item.code,
          weight: item.weightage,
        })) || [];

    const payload = {
      code: editingType?.code,
      name: editingType?.name,
      remote_utility_id: remoteUtilityId,
      criticality_assessment: criticalityAssessment,
    };
    await updateNetworkCriticality(payload);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const getCriticalityColor = (level: string) => {
    switch (level) {
      case "Low":
        return "bg-blue-100 text-blue-800";
      case "Medium":
        return "bg-yellow-100 text-yellow-800";
      case "High":
        return "bg-red-100 text-red-800";
      case "Critical":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // Loading state
  if (isCriticalityLoading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading criticality parameters...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Error state
  if (isCriticalityError) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <p className="text-red-600 mb-4">
                Failed to load criticality parameters.
              </p>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {title}
            {editingType?.name && (
              <span className="text-gray-600 font-normal ml-2">
                - {editingType?.name}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Introduction */}
          <div>
            <p className="text-sm text-gray-600">
              Adjust the sliders below to reflect the specific characteristics
              of this network. Each parameter is weighted and contributes to the
              overall criticality score.
            </p>
          </div>

          {/* Criticality Sliders */}
          <div>
            <h3 className="text-lg font-medium mb-4">Assessment Parameters</h3>

            {formFields.length > 0 ? (
              <DynamicForm
                fields={formFields}
                form={form}
                onFieldChange={handleFieldChange}
                config={{
                  showSubmitButton: false,
                  showResetButton: false,
                  layout: "vertical",
                  spacing: "comfortable",
                }}
              />
            ) : (
              <div className="text-center py-8 text-gray-500">
                No criticality parameters available.
              </div>
            )}
          </div>

          {/* Overall Score Display */}
          <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Overall Criticality Score
                </h3>
                <p className="text-3xl font-bold text-gray-900">
                  {criticalityScore}
                  <span className="text-lg text-gray-500 ml-1">/ 100</span>
                </p>
              </div>
              <Badge
                className={`text-lg px-4 py-2 ${getCriticalityColor(
                  criticalityLevel,
                )}`}
              >
                {criticalityLevel}
              </Badge>
            </div>
          </div>

          {/* Assessment Guidelines */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">
              Assessment Guidelines
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>
                • <strong>Score 1 (Very Low):</strong> Minimal impact on
                operations
              </li>
              <li>
                • <strong>Score 2 (Low):</strong> Low impact, can tolerate
                downtime
              </li>
              <li>
                • <strong>Score 3 (Moderate):</strong> Moderate impact, requires
                planning
              </li>
              <li>
                • <strong>Score 4 (High):</strong> High impact, critical for
                operations
              </li>
              <li>
                • <strong>Score 5 (Very High):</strong> Mission critical,
                immediate action required
              </li>
            </ul>
          </div>

          <DialogFooter className="sticky bottom-0 bg-white border-t flex justify-end">
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button onClick={handleSave} isLoading={isUpdating}>
                Save Assessment
              </Button>
            </div>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NetworkCriticalityModal;
